import { useEffect } from 'react';
import { useStore } from 'react-redux';

import { useAppDispatch } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import { checkAndUpdateCallee } from 'features/call/call-base/store';
import { CallDetailsStatus } from 'features/call/call-details/enums';
import { setCallDetailsCalleeStatus } from 'features/call/call-details/store';
import { findLatestHearingByPhoneNumber } from 'features/call/call-hearing/helpers';
import type {
  HearingParticipant,
  HearingPhone,
  HearingPhoneWithDisconnectReason,
  HearingPhoneWithId,
} from 'features/call/call-hearing/interfaces';
import {
  hearingsSelector,
  selectHasOtherActiveHearings,
} from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { useWhileComponentMounted } from 'features/utils/hook';

export function useCallDetailsStatusHandler() {
  const whileComponentMounted = useWhileComponentMounted();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();

  const findLiveHearing = ({
    id,
    phoneNumber,
  }: {
    id?: string;
    phoneNumber?: string;
  }): HearingParticipant | undefined => {
    const hearings = hearingsSelector(store.getState());

    if (id) {
      const hearingById = hearings.find((hearing) => hearing.id === id);

      if (hearingById) {
        return hearingById;
      }
    }

    if (!phoneNumber) {
      return undefined;
    }

    return findLatestHearingByPhoneNumber(hearings, phoneNumber);
  };

  const shouldIgnoreLateConnectEvent = (hearing?: HearingParticipant) =>
    Boolean(hearing?.teardownIntent) ||
    (hearing?.status === 'disconnected' &&
      hearing.lastTeardownReason === 'forced');

  const handleStatusHearingCalleeConnecting = (phoneNumber: HearingPhone) => {
    // Client-emitted $connecting is a reconnect — allow it even after a forced
    // teardown so call-details UI can leave reconnect state. Still block while
    // cancel/disconnect intent is active.
    if (findLiveHearing({ phoneNumber })?.teardownIntent) {
      return;
    }

    dispatch(
      setCallDetailsCalleeStatus({
        isDeafParticipant: false,
        status: CallDetailsStatus.CALLEE_CONNECTED,
      })
    );

    dispatch(checkAndUpdateCallee());
  };

  const handleStatusHearingCalleeConnected = ({
    id,
    phoneNumber,
  }: HearingPhoneWithId) => {
    if (shouldIgnoreLateConnectEvent(findLiveHearing({ id, phoneNumber }))) {
      return;
    }

    dispatch(
      setCallDetailsCalleeStatus({
        isDeafParticipant: false,
        status: CallDetailsStatus.CALLEE_CONNECTED,
      })
    );

    dispatch(checkAndUpdateCallee());
  };

  const handleStatusHearingCalleeReconnect = ({
    id,
    phoneNumber,
  }: HearingPhoneWithDisconnectReason) => {
    const state = store.getState();

    if (selectHasOtherActiveHearings(state, id ?? '', phoneNumber ?? '')) {
      return;
    }

    dispatch(
      setCallDetailsCalleeStatus({
        isDeafParticipant: false,
        status: CallDetailsStatus.CALLEE_RECONNECT,
      })
    );
  };

  const handleStatusDeafCalleeConnected = () => {
    dispatch(
      setCallDetailsCalleeStatus({
        isDeafParticipant: true,
        status: CallDetailsStatus.CALLEE_CONNECTED,
      })
    );
  };

  const handleStatusDeafCalleeReconnect = () => {
    dispatch(
      setCallDetailsCalleeStatus({
        isDeafParticipant: true,
        status: CallDetailsStatus.CALLEE_RECONNECT,
      })
    );
  };

  useEffect(() => {
    ParticipantsStatusEventBus.hearing.$connecting
      .pipe(whileComponentMounted())
      .subscribe(handleStatusHearingCalleeConnecting);

    ParticipantsStatusEventBus.hearing.$connected
      .pipe(whileComponentMounted())
      .subscribe(handleStatusHearingCalleeConnected);

    ParticipantsStatusEventBus.hearing.$disconnected
      .pipe(whileComponentMounted())
      .subscribe(handleStatusHearingCalleeReconnect);

    ParticipantsStatusEventBus.deaf.$connecting
      .pipe(whileComponentMounted())
      .subscribe(handleStatusDeafCalleeConnected);

    ParticipantsStatusEventBus.deaf.$connected
      .pipe(whileComponentMounted())
      .subscribe(handleStatusDeafCalleeConnected);

    ParticipantsStatusEventBus.deaf.$disconnected
      .pipe(whileComponentMounted())
      .subscribe(handleStatusDeafCalleeReconnect);

    // Event subscriptions are registered once. Hearing handlers read current
    // Redux state directly so late events respect active teardown markers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
